import { Grid, IconButton } from '@rbx/ui';
import React, { FC, useCallback, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { useLocalization, useTranslation } from '@rbx/intl';
import { useShareFeedbackProvider } from '@modules/feedback/context/ShareFeedbackProvider';
import {
  useProvidedConversationReducer,
  MessageHistoryItem,
  FeedbackRating,
  ConversationState,
} from '@rbx/conv-ai-provider';
import { CreateConversationResponse } from '@rbx/clients/convAiPlatformOrchestrationService';
import { createConversation } from '@modules/clients/convAi';
import useBotMessageActionBarStyles, { actionBarIconSize } from './BotMessageActionBar.styles';
import FeedbackNegativeIcon from '../../feedback/components/FeedbackNegativeIcon';
import FeedbackPositiveIcon from '../../feedback/components/FeedbackPositiveIcon';
import StreamingIndicator from './StreamingIndicator';
import getMessageLogParams from '../utils/getMessageLogParams';
import AssistantRegenerateIcon from './AssistantRegenerateIcon';

type BotMessageActionBarProps = {
  streaming: boolean;
  rating?: FeedbackRating;
  message: MessageHistoryItem;
};

const ThumbSelectDebounceTime = 250;

type RightBotMessageActionsProps = {
  message?: MessageHistoryItem;
  rating?: FeedbackRating;
};

const RightBotMessageActions: FC<React.PropsWithChildren<RightBotMessageActionsProps>> = ({
  message,
  rating: prevRating,
}) => {
  if (!message) throw new Error(`Missing message`);
  if (!message.messageId)
    throw new Error(`Missing message ID on message ${JSON.stringify(message)}`);
  const { translate } = useTranslation();
  const { openFeedbackDialog } = useShareFeedbackProvider();
  const { locale } = useLocalization();
  const [rating, setRating] = useState<FeedbackRating | undefined>(prevRating);
  const {
    classes: { iconWrapper },
  } = useBotMessageActionBarStyles();
  const {
    state: { conversationId },
    actions: { regenerateResponse, rateResponse },
  } = useProvidedConversationReducer();

  const messageLogParams = useMemo(
    () =>
      getMessageLogParams({ conversationId, message, locale, messageContentPropName: 'response' }),
    [message, locale, conversationId],
  );
  const onSubmitFeedback = useCallback(
    (ratingValue?: FeedbackRating, comment?: string) => {
      if (!ratingValue) throw new Error(`Missing rating value`);
      rateResponse(ratingValue, message, comment);
    },
    [message, rateResponse],
  );
  const getOnThumbSelect = useCallback(
    (newRating: FeedbackRating) => {
      const fn = () => {
        openFeedbackDialog(newRating, onSubmitFeedback);
        setRating(newRating);
      };
      return debounce(fn, ThumbSelectDebounceTime);
    },
    [openFeedbackDialog, onSubmitFeedback],
  );
  const promptAssistantResponse = useCallback(
    async (state: ConversationState): Promise<CreateConversationResponse> => {
      if (!state.activePromptMessage) throw new Error('No active prompt message');
      return createConversation({
        conversationId: state.conversationId,
        messageHistory: state.messageHistory as MessageHistoryItem[],
        messageToSend: state.activePromptMessage,
      });
    },
    [],
  );
  const onRegenerate = useCallback(() => {
    const originalPrompt = message.context.originalPrompt || message.context.prompt;
    if (originalPrompt) {
      regenerateResponse({
        originalPrompt,
        message,
        messageLogParams,
        promptAssistantResponse,
      });
    }
  }, [regenerateResponse, message, messageLogParams, promptAssistantResponse]);

  return (
    <Grid item XSmall={9} textAlign='right'>
      <IconButton
        color='secondary'
        size='medium'
        aria-label={translate('Label.RateResponsePositively')}
        className={iconWrapper}
        onClick={getOnThumbSelect(FeedbackRating.Positive)}>
        <FeedbackPositiveIcon
          size={actionBarIconSize}
          filled={rating === FeedbackRating.Positive}
        />
      </IconButton>
      <IconButton
        color='secondary'
        size='medium'
        aria-label={translate('Label.RateResponseNegatively')}
        className={iconWrapper}
        onClick={getOnThumbSelect(FeedbackRating.Negative)}>
        <FeedbackNegativeIcon
          size={actionBarIconSize}
          filled={rating === FeedbackRating.Negative}
        />
      </IconButton>
      <IconButton
        color='secondary'
        size='medium'
        aria-label={translate('RegenerateButton.AriaLabel')}
        className={iconWrapper}
        onClick={onRegenerate}>
        <AssistantRegenerateIcon
          size={actionBarIconSize}
          style={{
            padding: 1,
          }}
        />
      </IconButton>
    </Grid>
  );
};

const BotMessageActionBar: FC<React.PropsWithChildren<BotMessageActionBarProps>> = ({
  message,
  rating,
  streaming,
}) => {
  const {
    classes: { streamingIndicator },
  } = useBotMessageActionBarStyles();

  return (
    <Grid container className='message-action-bar'>
      <Grid item XSmall={3} alignItems='flex-start'>
        {streaming && <StreamingIndicator className={streamingIndicator} maxNumDots={3} />}
      </Grid>
      {!streaming && <RightBotMessageActions message={message} rating={rating} />}
    </Grid>
  );
};

export default BotMessageActionBar;
